import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LocalStorageService } from '../services/local-storage.service';
import { AuthService } from '../services/auth.service';
import { SessionVariable } from '../enum/session-variable';
import { NetworkStatusService } from '../services/network-status.service';
import { EnvService } from '../../env.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private authService = inject(AuthService);
  private networkStatusService = inject(NetworkStatusService);
  private envService = inject(EnvService);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
    null
  );

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.networkStatusService.isMonitoringStarted && this.networkStatusService.isOffline()) {
      return throwError({
        message: 'No internet connection. Please check your network and try again.',
        errorCode: 'OFFLINE',
      });
    }

    const urlsRequiringNoAuthChange = ['/kyc'];

    // Safeguard: Do not overwrite pre-existing Authorization headers (e.g. KYC Bearer tokens)
    if (
      request.headers.has('Authorization') &&
      (this.router.url.includes('/kyc') ||
        urlsRequiringNoAuthChange.some((url) => request.url.includes(url)))
    ) {
      return next.handle(request);
    }

    const TOKENKEY: string | null =
      this.localStorageService.getItem(SessionVariable.ACCESS_TOKEN) || null;
    const urlsRequiringBasic = [
      '/uaa/oauth/token',
      'check_token',
      '/accounts/payment/info',
      '/accounts/payment/validate-order-value',
      '/accounts/signup/init',
      '/accounts/signup/verifyOtp',
      '/accounts/signup/resendOtp',
      '/accounts/signup/verifyNationalId',
      '/accounts/signup/cinet/authentication',
      '/accounts/signup/nationalId/status',
      '/accounts/signup/customerInfo',
    ];

    const urlsRequiringNoToken = [
      '/pay/knet',
      '/pay/applePay',
      '/accounts/signup/token/otp',
      '/accounts/api/payment-links',
    ];

    const isBasicUrl = urlsRequiringBasic.some((url) => request.url.includes(url));
    const isNoTokenUrl = urlsRequiringNoToken.some((url) => request.url.includes(url));

    if (isBasicUrl || request.headers.has('refreshtoken') || !TOKENKEY) {
      request = this.addBasicToken(request);
    } else if (!isNoTokenUrl && TOKENKEY) {
      request = this.addToken(request, TOKENKEY);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          const errorDesc = error.error.error_description;
          if (errorDesc !== 'Password expired') {
            return this.handle401Error(request, next);
          }
        }

        let errorMsg: string;
        let errorCode: string | undefined;

        if (error.status === 500) {
          errorCode = error.status;
        }
        if (error.status === 0) {
          errorCode = 'OFFLINE';
          errorMsg = 'No internet connection. Please check your network and try again.';
          return throwError({ message: errorMsg, errorCode: errorCode });
        }

        let retryAfterSeconds: number | undefined;

        if (request?.responseType == 'text') {
          const err = JSON.parse(error.error);
          retryAfterSeconds = err?.retryAfterSeconds;
          if (error.status === 500) {
            errorCode = error?.error?.errorCode || error?.status;

            errorMsg = err?.message || 'Something went wrong, Please try again.';
          } else {
            errorMsg = err?.message || err?.error_description || error?.message;
          }
        } else {
          retryAfterSeconds = error?.error?.retryAfterSeconds;
          if (error.status === 500) {
            errorCode = error?.error?.errorCode || error?.status;

            errorMsg = error?.error?.message || 'Something went wrong, Please try again.';
          } else {
            errorCode = error?.error?.errorCode || error?.status;
            errorMsg = error?.error?.message || error?.error?.error_description || error?.message;
          }
        }

        return throwError({
          message: errorMsg,
          errorCode: errorCode,
          retryAfterSeconds: retryAfterSeconds,
        });
      })
    );
  }

  private addToken<T>(request: HttpRequest<T>, token: string | null): HttpRequest<T> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private addBasicToken<T>(request: HttpRequest<T>): HttpRequest<T> {
    const authHeader = this.getBasicAuthHeader(request.headers.get('requestFor'));
    const langHeader = this.getAcceptLanguageHeader();

    return request.clone({
      setHeaders: {
        Authorization: authHeader,
        'Accept-Language': langHeader,
      },
    });
  }

  // Common method to get Authorization header
  private getBasicAuthHeader(requestFor: string | null): string {
    if (requestFor === 'merchant') {
      return this.envService.merchantBasicAuth;
    }
    return this.envService.browserBasicAuth;
  }

  // Common method to get Accept-Language header
  private getAcceptLanguageHeader(): string {
    return this.localStorageService.getItem(SessionVariable.LANGUAGE) === 'ar-KW' ? 'ar' : 'en-US';
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authService.refreshToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token.access_token);
          return next.handle(this.addToken(request, token.access_token));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.localStorageService.clear();
          this.router.navigate(['/consumer/login']);
          return throwError(err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        }),
        catchError((err) => {
          this.localStorageService.clear();
          this.router.navigate(['/consumer/login']);
          return throwError(err);
        })
      );
    }
  }
}
